import { useAuth } from '@/contexts/AuthContext';
import { Crown, Shield, Eye, ChefHat, Check } from 'lucide-react';

const ROLE_CONFIG = {
  owner: { icon: Crown, label: 'Propietario', color: 'text-gold', desc: 'Acceso completo a todas las funciones' },
  chef: { icon: ChefHat, label: 'Chef', color: 'text-emerald-400', desc: 'Editar recetas e ingredientes' },
  viewer: { icon: Eye, label: 'Visualizador', color: 'text-blue-400', desc: 'Solo lectura' },
};

const PERMISSIONS = {
  owner: { can_edit: true, can_delete: true, can_manage_users: true, can_view_costs: true, can_export: true },
  chef: { can_edit: true, can_delete: false, can_manage_users: false, can_view_costs: false, can_export: false },
  viewer: { can_edit: false, can_delete: false, can_manage_users: false, can_view_costs: false, can_export: false },
};

export default function Team() {
  const { user } = useAuth();
  const roleConfig = ROLE_CONFIG[user?.role] || ROLE_CONFIG.owner;
  const permissions = PERMISSIONS[user?.role] || PERMISSIONS.owner;
  const Icon = roleConfig.icon;

  return (
    <div className="space-y-8 animate-fade-in max-w-2xl" data-testid="team-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Mi Cuenta</p>
        <h2 className="font-heading text-3xl font-bold">Perfil de Usuario</h2>
      </div>

      {/* Current User */}
      <div className="bg-[#0A0A0A] border border-white/5 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-gold/20 flex items-center justify-center text-gold text-2xl font-bold">
            {user?.name?.[0]?.toUpperCase() || 'U'}
          </div>
          <div>
            <p className="text-lg font-semibold">{user?.name || 'Usuario'}</p>
            <p className="text-sm text-white/40">{user?.email}</p>
            <div className={`inline-flex items-center gap-2 mt-2 px-3 py-1 ${roleConfig.color} bg-white/5`}>
              <Icon size={14} />
              <span className="text-xs uppercase tracking-widest">{roleConfig.label}</span>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-4">
          <p className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Tus Permisos</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-center gap-2">
              <Check size={14} className={permissions.can_edit ? 'text-emerald-400' : 'text-white/20'} />
              <span className={`text-sm ${permissions.can_edit ? 'text-white/60' : 'text-white/20'}`}>Editar datos</span>
            </div>
            <div className="flex items-center gap-2">
              <Check size={14} className={permissions.can_delete ? 'text-emerald-400' : 'text-white/20'} />
              <span className={`text-sm ${permissions.can_delete ? 'text-white/60' : 'text-white/20'}`}>Eliminar datos</span>
            </div>
            <div className="flex items-center gap-2">
              <Check size={14} className={permissions.can_view_costs ? 'text-emerald-400' : 'text-white/20'} />
              <span className={`text-sm ${permissions.can_view_costs ? 'text-white/60' : 'text-white/20'}`}>Ver costos</span>
            </div>
            <div className="flex items-center gap-2">
              <Check size={14} className={permissions.can_export ? 'text-emerald-400' : 'text-white/20'} />
              <span className={`text-sm ${permissions.can_export ? 'text-white/60' : 'text-white/20'}`}>Exportar reportes</span>
            </div>
          </div>
        </div>
      </div>

      {/* Account Info */}
      <div className="bg-[#0A0A0A] border border-white/5 p-6">
        <p className="text-[10px] uppercase tracking-widest text-white/40 mb-4">Informacion de Cuenta</p>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-sm text-white/40">Pais</span>
            <span className="text-sm">{user?.country || 'No especificado'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-white/40">Moneda</span>
            <span className="text-sm text-gold">{user?.currency || 'USD'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-white/40">Plan</span>
            <span className={`text-sm ${user?.subscription_status === 'active' ? 'text-gold' : 'text-white/40'}`}>
              {user?.subscription_status === 'active' ? 'Professional' : 'Prueba'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-white/40">Tipo Restaurante</span>
            <span className="text-sm">{user?.restaurant_type || 'normal'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-white/40">Miembro desde</span>
            <span className="text-sm text-white/60">{user?.created_at?.slice(0, 10)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
